import { uniqueId } from 'lodash-es';
import React, { PropsWithChildren, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { DefaultTooltip, TooltipButton } from 'components/common/tooltip/Tooltip';
import { useServiceDescriptionLink } from 'components/hooks/services/useServiceDescriptionLink';
import { TrackedFeature, TrackedPage, TRACKING_PARAM_KEYS } from 'services/Logger';
import { useLoggerTracking } from 'components/hooks/logger/useLoggerTracking';
import { useBoardServiceForDetail } from 'components/hooks/services/useBoardServices';

export const TooltipInfoLink = ({
    serviceKey,
    isSgmService,
    tooltipText,
}: {
    serviceKey?: string | null;
    isSgmService: boolean;
    tooltipText?: string | null;
}) => {
    const { hasSgmServiceKey } = useBoardServiceForDetail();

    if (!isSgmService) {
        return <NonSgmServiceInfoLinkWithTooltip tooltipText={tooltipText} />;
    }
    if (!serviceKey) {
        return <SgmServiceNoDetailsInfoLinkWithTooltip />;
    }

    const service = hasSgmServiceKey(serviceKey);
    if (!service) {
        return <SgmServiceNoDetailsInfoLinkWithTooltip />;
    }
    return (
        <SgmServiceInfoLink serviceKey={serviceKey} tooltipText={tooltipText} />
    );
};

type InfoLinkProps = {
    serviceKey: Nullable<string>;
    className?: string;
    iconClassName?: string;
    linkId?: string;
    tooltipText?: Nullable<string>;
    inWidgetContext?: boolean;
};

export const SgmServiceInfoLink: React.FunctionComponent<PropsWithChildren<InfoLinkProps>> = ({
    serviceKey,
    className,
    iconClassName,
    children,
    tooltipText,
    inWidgetContext = false,
}) => {
    const linkId = useMemo(() => uniqueId('infoLink'), []);
    const descriptionUrl = useServiceDescriptionLink(serviceKey);
    const { t: translate } = useTranslation('service-list');
    const { hasSgmServiceKey } = useBoardServiceForDetail();
    const track = useLoggerTracking();
    const hasInfoService = hasSgmServiceKey(serviceKey);

    if (!hasInfoService) {
        return null;
    }
    const message = tooltipText ? tooltipText : 'tooltip:viewDetailsSgmService';
    const label = translate(message);

    return <DefaultTooltip text={label}>
        <Link
            id={linkId}
            to={descriptionUrl}
            onClick={() => {

                if (inWidgetContext) {
                    track(TrackedPage.Widgets, TrackedFeature.OpenServiceDetail, { [TRACKING_PARAM_KEYS.serviceKey]: serviceKey || '' });
                }
                else {
                    track(TrackedPage.ServiceDetail, TrackedFeature.OpenServiceDetail, { [TRACKING_PARAM_KEYS.serviceKey]: serviceKey || '' });
                }
            }}
            className={
                className ||
                'btn btn-flat-secondary btn-lg overflow-hidden all-services-button-width'
            }
        >
            {children || <span className="visually-hidden">{label}</span>}
            <i className={iconClassName || 'icon'}>info_outline</i>
        </Link>
    </DefaultTooltip>;
};

const DefaultDisabledInfoLink = ({ tooltipId }: { tooltipId: string }) => (
    <>
        <TooltipButton
            tooltipId={tooltipId}
            className="btn overflow-hidden all-services-button-width disabled"
            type="button"
            variant="flat-secondary"
        >
            <i className={'icon'}>info_outline</i>
        </TooltipButton>
    </>
);

type NonSgmServiceInfoLinkWithTooltipProps = {
    tooltipText?: Nullable<string>
};

const NonSgmServiceInfoLinkWithTooltip: React.FunctionComponent<NonSgmServiceInfoLinkWithTooltipProps> = ({
    tooltipText,
}) => {
    const message = tooltipText ? tooltipText : 'tooltip:viewDetailsNonSgmService';
    return <DefaultDisabledInfoLink tooltipId={message} />;
};

const SgmServiceNoDetailsInfoLinkWithTooltip = () => (
    <DefaultDisabledInfoLink tooltipId="tooltip:viewDetailsSgmService" />
);